// src/utils/liveNewbies.ts
//
// Nováček v živém závodě – zápisy z nastavení závodu (fáze 2 je přesunula
// z LiveRaceSetup beze změny chování; obrazovka rozhodčího je převezme ve
// fázi 3 – dodatek kroku 12 plánu: „zapisovat jako LiveRaceSetup“).
//
// Rozhoduje veřejná startovní listina races/{raceId}/registrations/{uid}:
// čte ji tabule, rozhodčí i publikace; hlavní rozhodčí v ní smí měnit jen
// nováčka (pravidla). Přihláška (kořenová registrations) je jen vstup její
// synchronizace – zapisuje se, když ji střelec má a zapisující ji smí číst
// (správce, hlavní rozhodčí závodu); chyba přihlášky nevadí.

import { collection, doc, getDocs, query, setDoc, where, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { reportWarning } from "./reportError";
import { startListPath } from "./startList";

export type NewbieValue = "ano" | "ne";

/**
 * Nováček v přihláškách střelce na závod (všechny – staré duplicity, ať
 * souhlasí). Chyba se jen zaznamená.
 */
export async function setRegistrationNewbie(raceId: string, userId: string, isNewbie: NewbieValue): Promise<void> {
  try {
    const snap = await getDocs(
      query(collection(db, "registrations"), where("raceId", "==", raceId), where("userId", "==", userId))
    );
    const changed = snap.docs.filter((d) => d.get("isNewbie") !== isNewbie);
    if (changed.length === 0) return;
    const batch = writeBatch(db);
    changed.forEach((d) => batch.update(d.ref, { isNewbie }));
    await batch.commit();
  } catch (err) {
    reportWarning("liveNewbies.registrationNewbie", err, { raceId, userId });
  }
}

/**
 * Nováček ve startovní listině závodu – vždy, i střelci přidanému ručně.
 * Chyba listiny se vyhodí (volající ji ohlásí). Přihláška se zapisuje
 * souběžně, ne až po potvrzení listiny (fáze 3): bez signálu se tak do
 * fronty zařadí obě (dřív přihláška čekala na server a po zabití aplikace
 * se nezapsala nikdy); její chyba se jen zaznamená.
 */
export async function setStartListNewbie(
  raceId: string,
  userId: string,
  isNewbie: NewbieValue,
  options: { updateRegistration: boolean }
): Promise<void> {
  const row = setDoc(doc(db, startListPath(raceId), userId), { userId, raceId, isNewbie }, { merge: true });
  const registration = options.updateRegistration ? setRegistrationNewbie(raceId, userId, isNewbie) : Promise.resolve();
  await Promise.all([row, registration]);
}

/**
 * Trvalý zákaz nováčka (jen správce – zápis do cizího profilu): profil
 * i řádek startovní listiny v JEDNÉ dávce. Řádky dalších závodů střelce
 * doplní jejich synchronizace (zákaz z profilu se přenáší). Chyba dávky se
 * vyhodí; přihláška souběžně (bez signálu se zařadí do fronty taky).
 */
export async function disableNewbiePermanently(
  raceId: string,
  userId: string,
  options: { updateRegistration: boolean }
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, "users", userId), { newbieDisabled: true });
  batch.set(doc(db, startListPath(raceId), userId), { userId, raceId, newbieDisabled: true, isNewbie: "ne" }, { merge: true });
  const committed = batch.commit();
  const registration = options.updateRegistration ? setRegistrationNewbie(raceId, userId, "ne") : Promise.resolve();
  await Promise.all([committed, registration]);
}
